// Matches the "xl" breakpoint, where the hamburger hides and the desktop nav
// takes over.
const DESKTOP_BREAKPOINT = 1140;
const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function initMobileNav() {
  const mobileNav = document.querySelector(".js-mobile-nav");
  const hamburger = document.querySelector(".js-hamburger");
  const siteTop = document.querySelector(".site-top");

  // Prevent page-jump on # parent links (also covers desktop hover menus).
  document.addEventListener("click", (e) => {
    if (e.target.closest('.menu-item-has-children > a[href="#"]')) {
      e.preventDefault();
    }
  });

  if (!mobileNav || !hamburger) return;

  const isOpen = () => mobileNav.classList.contains("is-active");

  // Focusable elements with a rendered box (mirrors jQuery's :visible).
  const getFocusable = () =>
    Array.from(mobileNav.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.getClientRects().length > 0
    );

  // The panel sits under the sticky header, whose alert banner can be
  // dismissed, so its bottom edge is republished rather than measured once.
  function syncSiteTop() {
    if (!siteTop) return;
    document.documentElement.style.setProperty(
      "--aviatrix-site-top",
      Math.round(siteTop.getBoundingClientRect().bottom) + "px"
    );
  }

  // Open the section the visitor is in, so the current page is visible without
  // expanding it by hand. closeNav() collapses everything again.
  function expandCurrentSection() {
    mobileNav
      .querySelectorAll(
        ".nav__list > .menu-item-has-children.current-menu-ancestor, .nav__list > .menu-item-has-children.current-menu-parent"
      )
      .forEach((item) => {
        const link = item.querySelector(":scope > a");
        const submenu = item.querySelector(":scope > .sub-menu");
        if (!link || !submenu) return;
        link.setAttribute("aria-expanded", "true");
        submenu.removeAttribute("hidden");
        item.classList.add("menu-item--active");
      });
  }

  function closeNav() {
    mobileNav.classList.remove("is-active");
    mobileNav.setAttribute("aria-hidden", "true");
    hamburger.classList.remove("is-active");
    hamburger.setAttribute("aria-expanded", "false");
    document.body.classList.remove("aviatrix-nav-open");
    document.body.style.overflowY = "";

    // Collapse any expanded first-level submenus
    mobileNav
      .querySelectorAll(".nav__list > .menu-item--active")
      .forEach((item) => item.classList.remove("menu-item--active"));
    mobileNav
      .querySelectorAll(".nav__list > .menu-item-has-children > a")
      .forEach((link) => link.setAttribute("aria-expanded", "false"));
    mobileNav
      .querySelectorAll(".nav__list > .menu-item-has-children > .sub-menu")
      .forEach((submenu) => submenu.setAttribute("hidden", ""));
  }

  function openNav() {
    mobileNav.classList.add("is-active");
    mobileNav.setAttribute("aria-hidden", "false");
    hamburger.classList.add("is-active");
    hamburger.setAttribute("aria-expanded", "true");
    document.body.classList.add("aviatrix-nav-open");
    document.body.style.overflowY = "hidden";
    syncSiteTop();
    expandCurrentSection();
    // Move focus into the nav for keyboard and screen reader users. Deferred a
    // frame: the panel's controls transition their inherited visibility, and
    // an element that is still visibility:hidden refuses focus, which left
    // focus sitting on the hamburger until the first Tab.
    const focusFirst = () => {
      const focusable = getFocusable();
      if (focusable.length) focusable[0].focus();
      if (!mobileNav.contains(document.activeElement)) {
        mobileNav.setAttribute("tabindex", "-1");
        mobileNav.focus();
      }
    };
    window.requestAnimationFrame(() => window.requestAnimationFrame(focusFirst));
  }

  hamburger.addEventListener("click", (e) => {
    e.preventDefault();
    if (isOpen()) {
      closeNav();
    } else {
      openNav();
    }
  });

  // Focus trap + Escape while the nav is open
  document.addEventListener("keydown", (e) => {
    if (!isOpen()) return;

    if (e.key === "Escape") {
      closeNav();
      hamburger.focus();
      return;
    }

    if (e.key !== "Tab") return;

    const focusable = getFocusable();
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else if (document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  // A tap on the dimmed page closes the panel.
  document.addEventListener("click", (e) => {
    if (!isOpen()) return;
    if (e.target.closest(".js-mobile-nav, .site-top, .js-hamburger")) return;
    closeNav();
  });

  // Close if the viewport crosses to desktop while open
  window.addEventListener("resize", () => {
    syncSiteTop();
    if (window.innerWidth >= DESKTOP_BREAKPOINT && isOpen()) {
      closeNav();
    }
  });

  syncSiteTop();
  if (siteTop && "ResizeObserver" in window) {
    new ResizeObserver(syncSiteTop).observe(siteTop);
  }

  // Submenu disclosure toggles: parent links expand/collapse their .sub-menu
  mobileNav
    .querySelectorAll(".nav__list > .menu-item-has-children")
    .forEach((item, index) => {
      const link = item.querySelector(":scope > a");
      const submenu = item.querySelector(":scope > .sub-menu");
      if (!link || !submenu) return;

      submenu.id = "mobile-submenu-" + index;
      link.setAttribute("role", "button");
      link.setAttribute("aria-expanded", "false");
      link.setAttribute("aria-controls", submenu.id);
      submenu.setAttribute("hidden", "");

      const toggleSubmenu = () => {
        const expanded = link.getAttribute("aria-expanded") === "true";
        link.setAttribute("aria-expanded", String(!expanded));
        if (expanded) {
          submenu.setAttribute("hidden", "");
          item.classList.remove("menu-item--active");
        } else {
          submenu.removeAttribute("hidden");
          item.classList.add("menu-item--active");
        }
      };

      link.addEventListener("click", (e) => {
        e.preventDefault();
        toggleSubmenu();
      });
      // Space for role="button" (Enter already fires click on links)
      link.addEventListener("keydown", (e) => {
        if (e.key === " ") {
          e.preventDefault();
          toggleSubmenu();
        }
      });
    });

  mobileNav.setAttribute("aria-hidden", "true");
}
